import { useEffect, useState } from 'react'
import { useSubscription } from '../contexts/SubscriptionContext'
import { selectBetaIndustry } from '../api/subscription'
import { listIndustries } from '../api/industries'
import type { IndustryListItem } from '../types'

const PURPLE = '#7C3AED'
const PURPLE_LIGHT = '#A78BFA'
const SURFACE = '#111118'
const BORDER = 'rgba(124, 58, 237, 0.3)'
const TEXT_MUTED = '#9CA3AF'

export default function BetaIndustrySelectionModal() {
  const { needsIndustrySelection, refetch } = useSubscription()
  const [industries, setIndustries] = useState<IndustryListItem[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!needsIndustrySelection) return
    let cancelled = false
    setLoading(true)
    listIndustries()
      .then((data) => { if (!cancelled) setIndustries(data) })
      .catch(() => { if (!cancelled) setError('Could not load industries. Please refresh.') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [needsIndustrySelection])

  if (!needsIndustrySelection) return null

  async function handleSubmit() {
    if (!selected || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await selectBetaIndustry(selected)
      await refetch()
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Could not save your selection. Please try again.')
      setSubmitting(false)
    }
  }

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.85)',
      backdropFilter: 'blur(4px)',
      zIndex: 9999,
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch',
      fontFamily: 'Inter, sans-serif',
    }}>
      <div style={{
        background: SURFACE,
        border: `1px solid ${BORDER}`,
        borderRadius: 16,
        padding: 'clamp(24px, 4vw, 40px)',
        maxWidth: 760,
        width: 'calc(100% - 40px)',
        color: '#fff',
        boxShadow: `0 30px 80px ${PURPLE}33`,
        margin: '32px auto',
      }}>
        <h2 style={{
          fontFamily: 'Syne, sans-serif',
          fontSize: 'clamp(22px, 4vw, 32px)',
          fontWeight: 800,
          margin: '0 0 16px',
          lineHeight: 1.2,
        }}>
          One quick step
        </h2>
        <p style={{
          color: TEXT_MUTED,
          fontSize: 15,
          lineHeight: 1.6,
          margin: '0 0 28px',
        }}>
          We've simplified how subscriptions work at Aspired AI. As a beta user,
          nothing has changed about your access — you still have free, unlimited
          use of all 10 core agents and 4 industry-specific agents, with no charge.
          The only thing we need from you is which industry you're in, so we can
          unlock the right specialist agents for your business.
        </p>

        {loading && (
          <div style={{ color: TEXT_MUTED, padding: '20px 0', textAlign: 'center' }}>
            Loading industries…
          </div>
        )}

        {!loading && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: 12,
            marginBottom: 24,
          }}>
            {industries.map((ind) => {
              const isSelected = selected === ind.slug
              return (
                <button
                  key={ind.slug}
                  type="button"
                  onClick={() => setSelected(ind.slug)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '14px 14px',
                    background: isSelected ? `${PURPLE}26` : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${isSelected ? PURPLE : 'rgba(255,255,255,0.10)'}`,
                    borderRadius: 10,
                    color: '#fff',
                    fontFamily: 'inherit',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background 0.15s, border-color 0.15s',
                  }}
                >
                  <span style={{ fontSize: 22, lineHeight: 1 }}>{ind.emoji || '✨'}</span>
                  <span>{ind.name}</span>
                </button>
              )
            })}
          </div>
        )}

        {error && (
          <div style={{
            color: '#FCA5A5',
            background: 'rgba(239,68,68,0.10)',
            border: '1px solid rgba(239,68,68,0.30)',
            padding: '10px 14px',
            borderRadius: 8,
            fontSize: 14,
            marginBottom: 16,
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
          {selected && (
            <span style={{ color: PURPLE_LIGHT, fontSize: 13 }}>
              You can change this anytime in Settings.
            </span>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!selected || submitting}
            style={{
              background: !selected || submitting ? 'rgba(124,58,237,0.4)' : PURPLE,
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '12px 28px',
              fontFamily: 'Syne, sans-serif',
              fontWeight: 700,
              fontSize: 15,
              cursor: !selected || submitting ? 'not-allowed' : 'pointer',
              transition: 'background 0.15s',
            }}
          >
            {submitting ? 'Saving…' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  )
}
